import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import Index from "./pages/Index.tsx";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { useCartSync } from "@/hooks/useCartSync";

const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const Drops = lazy(() => import("./pages/Drops.tsx"));
const Hats = lazy(() => import("./pages/Hats.tsx"));
const League = lazy(() => import("./pages/League.tsx"));
const Exclusives = lazy(() => import("./pages/Exclusives.tsx"));
const Accessories = lazy(() => import("./pages/Accessories.tsx"));
const Sale = lazy(() => import("./pages/Sale.tsx"));
const Classics = lazy(() => import("./pages/Classics.tsx"));
const SignIn = lazy(() => import("./pages/SignIn.tsx"));
const OrderStatus = lazy(() => import("./pages/OrderStatus.tsx"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const TeamHat = lazy(() => import("./pages/TeamHat.tsx"));
const Collection = lazy(() => import("./pages/Collection.tsx"));
const Favourites = lazy(() => import("./pages/Favourites.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const Shipping = lazy(() => import("./pages/help/Shipping.tsx"));
const Returns = lazy(() => import("./pages/help/Returns.tsx"));
const Privacy = lazy(() => import("./pages/help/Privacy.tsx"));
const Terms = lazy(() => import("./pages/help/Terms.tsx"));
const Support = lazy(() => import("./pages/help/Support.tsx"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const AppRoutes = () => {
  useCartSync();
  return (
    <Suspense fallback={
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
      </div>
    }>
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Index />} />
        <Route path="/drops" element={<Drops />} />
        <Route path="/hats" element={<Hats />} />
        <Route path="/league" element={<League />} />
        <Route path="/exclusives" element={<Exclusives />} />
        <Route path="/accessories" element={<Accessories />} />
        <Route path="/sale" element={<Sale />} />
        <Route path="/classics" element={<Classics />} />
        <Route path="/account/sign-in" element={<SignIn />} />
        <Route path="/account/order-status" element={<OrderStatus />} />
        <Route path="/account/forgot-password" element={<ForgotPassword />} />
        <Route path="/account/reset-password" element={<ResetPassword />} />
        <Route path="/account/profile" element={<Profile />} />
        <Route path="/help/shipping" element={<Shipping />} />
        <Route path="/help/returns" element={<Returns />} />
        <Route path="/help/privacy" element={<Privacy />} />
        <Route path="/help/terms" element={<Terms />} />
        <Route path="/help/support" element={<Support />} />
        <Route path="/product/:handle" element={<ProductDetail />} />
        <Route path="/team/:slug" element={<TeamHat />} />
        <Route path="/collections/:slug" element={<Collection />} />
        <Route path="/favourites" element={<Favourites />} />
        <Route path="/unsubscribe" element={<Unsubscribe />} />
      </Route>
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
